"use client";

import { useSyncExternalStore } from "react";

/**
 * The single answer to "is it time to fire analytics yet?".
 *
 * PERF: this used to be computed independently by every consumer. `useShouldFire`
 * was written as a local hook but answers a global question, and it had **eleven**
 * call sites reachable from the root layout — PixelsHeadScripts, PixelsOtherScripts,
 * PageViewPixel, ResultClient and the seven pixel hooks. Each one scheduled its own
 * `requestIdleCallback`, subscribed to `usePathname()` + `useSearchParams()`, and held
 * its own `useState`. So every page paid for 11 idle callbacks competing for the same
 * idle slices, 11 router subscriptions re-rendering on every navigation, and 11 state
 * flips when those callbacks resolved. The batched idle callbacks are also part of why
 * all the tags were released in one frame (TPT-1).
 *
 * Same shape as `hooks/IsDomReady.ts`: one module-level store, one scheduler, and
 * `useSyncExternalStore` so consumers share it for free.
 */

let idle = false;
/**
 * True on conversion-critical pages (checkout result / `track_id`), where analytics
 * deliberately fire on DOM-ready instead of waiting for idle. Fed by `TagGateSync`,
 * which owns the only router subscription.
 */
let critical = false;
let scheduled = false;

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/**
 * Start the idle timer. Called on first subscribe rather than at module scope so a
 * module import cannot schedule work on a page that never reads the gate.
 * Timings are unchanged from the previous per-consumer implementation.
 */
const scheduleIdle = () => {
  if (scheduled || typeof window === "undefined") return;
  scheduled = true;
  const ric = (window as any).requestIdleCallback as
    ((cb: () => void, opts?: { timeout: number }) => number) | undefined;
  const flip = () => {
    if (idle) return;
    idle = true;
    emit();
  };
  if (ric) ric(flip, { timeout: 2500 });
  else setTimeout(flip, 1500);
};

/** Called by `TagGateSync` only. */
export const setConversionCritical = (next: boolean) => {
  if (critical === next) return;
  critical = next;
  emit();
};

const subscribe = (onStoreChange: () => void) => {
  scheduleIdle();
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
};

const getSnapshot = () => idle;
const getCriticalSnapshot = () => critical;
const getServerSnapshot = () => false;

/** Browser is idle after first paint. */
export const useIdleReady = () =>
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

/** Current route is conversion-critical. */
export const useConversionCritical = () =>
  useSyncExternalStore(subscribe, getCriticalSnapshot, getServerSnapshot);
